import { useCallback, useEffect, useState, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
  );
}

/**
 * Traps Tab/Shift+Tab focus inside `containerRef` while `active`, moves focus
 * in on activation, and restores focus to whatever had it beforehand on
 * deactivation. Used by Modal and the mobile sidebar drawer.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const initial = getFocusable(container);
    (initial[0] ?? container).focus();

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Tab' || !container) return;
      const items = getFocusable(container);
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        return;
      }
      const current = document.activeElement;
      if (e.shiftKey) {
        if (current === first || !container.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last || !container.contains(current)) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [active, containerRef]);
}

/** Calls `handler` on Escape while `active`. */
export function useEscapeKey(handler: () => void, active = true): void {
  useEffect(() => {
    if (!active) return undefined;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') handler();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handler, active]);
}

/** Calls `handler` for a pointerdown outside `ref`'s element. */
export function useOnClickOutside(ref: RefObject<HTMLElement | null>, handler: () => void, active = true): void {
  useEffect(() => {
    if (!active) return undefined;
    function onPointerDown(e: PointerEvent): void {
      const el = ref.current;
      if (!el || (e.target instanceof Node && el.contains(e.target))) return;
      handler();
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [ref, handler, active]);
}

/**
 * `useState` backed by `localStorage`, for per-viewer UX conveniences only
 * (sidebar collapsed state, not anything server-authoritative). Every access
 * is wrapped in try/catch: private browsing, blocked storage and disabled
 * cookies can all throw or silently no-op.
 */
export function useLocalStorageState<T>(key: string, initial: T): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const setAndPersist = useCallback(
    (value: T) => {
      setState(value);
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Storage unavailable (private mode, quota, disabled) — state still
        // works for this render, it just won't survive a reload.
      }
    },
    [key],
  );

  return [state, setAndPersist];
}

/** True once the viewport is narrower than `breakpointPx`. Used for the sidebar -> drawer switch and Table's card fallback. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (): void => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
